import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { siteWide } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import { h2, link, paragraphRegular } from '@helpers/typography';
import RegionSelectorStyles from '@stories/Components/Misc/RegionSelector/RegionSelector.styles';
import SocialLinksStyles from '@stories/Components/Misc/SocialLinks/SocialLinks.styles';
import { m } from 'framer-motion';
import styled, { css } from 'styled-components';
const Container = styled.footer `
  position: relative;
  z-index: 1;
  overflow: hidden;

  @media print {
    display: none;
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 0;
    z-index: -1;
  }

  &::before {
    ${transition('transform', 0.5)};

    bottom: 0;
    background-color: ${brand.primary.purple.light};
    width: 300%;
    aspect-ratio: 1;
    transform: translate(-25%, 0%) rotate(40deg);

    @media ${from(Device.TabletLarge)} {
      width: 1800px;
      border-top-right-radius: 120px;
      transform: translate(-27.5%, -80%) rotate(40deg) skew(0deg, 10deg);
      transform-origin: bottom left;
      bottom: auto;
      top: 0;
      right: 0;
      left: auto;
    }

    @media ${from(Device.DesktopLarge)} {
      transform: translate(-35%, -65%) rotate(40deg) skew(0deg, 5deg);
    }
  }

  &::after {
    ${transition('transform', 0.65)};

    background-color: ${brand.primary.blue.light};
    mix-blend-mode: multiply;
    bottom: 0;
    width: 100%;
    aspect-ratio: 1;
    transform: translate(-66%, 66%) rotate(40deg);
    transition-delay: 500ms;
    border-radius: 60px;

    @media ${from(Device.TabletLarge)} {
      right: 0;
      left: auto;
      transform: translate(50%, 40%) rotate(40deg);
      aspect-ratio: 0.9;
      width: 850px;
    }

    @media ${from(Device.DesktopLarge)} {
      transform: translate(55%, 57.5%) rotate(40deg);
    }
  }

  &[data-animate='false'] {
    &::before {
      transform: translate(-25%, 0%) rotate(0deg);
    }

    &::after {
      transform: translate(50%, 125%) rotate(40deg);

      @media ${from(Device.TabletLarge)} {
        transform: translate(0%, 125%) rotate(40deg);
      }
    }
  }
`;
const Inner = styled.div `
  ${siteWide};

  --logoWidth: ${fluid(88, 167)};

  display: grid;
  color: ${brand.global.white};
  padding-top: ${fluid(40, 44)};
  padding-bottom: ${fluid(28, 32)};
  grid-template-columns: min-content auto min-content;

  @media ${from(Device.TabletLarge)} {
    grid-template-columns: repeat(6, 1fr);
    column-gap: ${fluid(16, 20)};
  }

  &::before {
    content: '';
    position: absolute;
    z-index: -1;
    top: 0;
    left: 0;
    width: 100%;
    height: 50%;
    background-color: ${brand.primary.purple.light};

    @media ${from(Device.TabletLarge)} {
      height: 100%;
      width: 25%;
    }

    @media ${from(Device.DesktopLarge)} {
      width: 67%;
    }
  }

  ${RegionSelectorStyles.Container} {
    grid-column: 1 / span 3;
    color: ${brand.global.white};
    margin-bottom: 40px;

    @media ${until(Device.TabletLarge)} {
      ${RegionSelectorStyles.Button} {
        justify-content: center;
        width: 100%;
      }

      ${RegionSelectorStyles.Regions} {
        left: 50%;
        transform: translateX(-50%);
      }

      &:has(${RegionSelectorStyles.Button}[aria-expanded='false']) ${RegionSelectorStyles.Regions} {
        transform: translate(-50%, 20px);
      }
    }

    @media ${from(Device.TabletLarge)} {
      grid-row: 2;
      margin-bottom: ${fluid(28, 34)};
    }
  }
`;
const Logo = styled.a `
  display: block;
  width: var(--logoWidth);
  flex: 0 0 var(--logoWidth);
  grid-column: 1;
  grid-row: 1;

  @media ${from(Device.TabletLarge)} {
    margin-bottom: ${fluid(24, 32)};
  }
`;
const Strapline = styled.p `
  ${fonts.lato.bold};

  grid-column: 2 / span 1;
  grid-row: 1;
  font-size: ${fluid(13, 20)};
  line-height: ${fluid(19, 28)};
  margin: auto 16px auto 24px;

  @media ${from(Device.TabletLarge)} {
    margin: 0;
  }
`;
const StraplineLarge = styled.p `
  ${h2};

  grid-column: 1 / span 3;
  margin: 24px 0;

  > span {
    ${transition('background-size, color', 0.5)};

    display: inline;
    background-image: linear-gradient(
      to right,
      ${brand.primary.blue.dark},
      ${brand.primary.blue.dark}
    );
    background-repeat: no-repeat;
    background-size: 100% 100%;
    transition-delay: 1s;
    padding: 0 8px;
    margin-left: -8px;

    ${Container}[data-animate='false'] & {
      background-size: 0% 100%;
    }
  }

  @media ${from(Device.TabletLarge)} {
    grid-column: 3 / span 2;
    margin: 0;
  }
`;
const ButtonWrapper = styled.div `
  grid-column: 1 / span 3;
  display: flex;
  gap: ${fluid(8, 32)};
  flex-wrap: wrap;

  @media ${from(Device.TabletLarge)} {
    grid-column: 4 / span 2;
    grid-row: 6;
    margin: auto 0 auto auto;
  }
`;
const LinkListContainer = styled.div `
  grid-column: 1 / span 3;
  margin: 55px 0 40px;

  @media ${from(Device.TabletLarge)} {
    grid-column: 1 / span 6;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: ${fluid(16, 20)};
    margin: ${fluid(34, 46)} 0 ${fluid(36, 52)};

    > div[data-long='true'] {
      grid-row: span 2;
    }
  }

  @media ${from(Device.DesktopLarge)} {
    grid-column: 1 / span 5;
    grid-template-columns: repeat(5, 1fr);
  }
`;
const footerLinkStyles = css `
  ${link};

  --textLinkColour: ${brand.global.white};
`;
const LinkListTitle = styled.h2 `
  ${fonts.lato.bold};

  font-size: ${fluid(15, 20)};
  line-height: 1.2;
`;
const LinkListHeader = styled.button `
  ${ButtonReset};

  display: block;
  color: currentColor;
  margin: 0 0 ${fluid(15, 25)};

  h2 {
    ${fonts.lato.bold};

    font-size: ${fluid(15, 20)};
    line-height: 1.2;
  }

  button& {
    display: flex;
    align-items: center;
    width: 100%;
    text-align: left;
    gap: 24px;
    justify-content: space-between;
    padding: 0 0 15px;
    border-bottom: 1px solid ${brand.primary.blue.dark};

    &:focus-visible {
      outline: auto;
      outline-offset: 5px;
      outline-width: 1px;
    }

    &[data-open='true'] svg {
      transform: rotate(180deg);
    }

    /* Mobile click styles - button should mimic press */
    @media ${until(Device.TabletLarge)} {
      ${transition('transform', 0.15)};

      &:active {
        transform: translateY(1.5px);
      }
    }

    @media ${from(Device.TabletLarge)} {
      display: none;
    }
  }

  a& {
    ${footerLinkStyles};
  }

  a&,
  span& {
    @media ${until(Device.TabletLarge)} {
      display: none;
    }
  }

  svg {
    ${transition('transform', 0.25)};

    display: block;
    width: 24px;
  }
`;
const LinkList = styled(m.ul) `
  display: flex;
  flex-direction: column;
  gap: ${fluid(16, 24)};
  overflow: hidden;

  li {
    ${fonts.lato.regular};

    font-size: ${fluid(14, 16)};
    line-height: ${fluid(18, 20)};
  }

  a {
    ${footerLinkStyles};

    &:has(svg) {
      line-height: 24px;
    }

    svg {
      display: inline-block;
      width: 24px;
      margin: 0 8px -8px 0;
    }
  }

  @media ${from(Device.TabletLarge)} {
    height: auto !important;
    padding-bottom: 3px;
    margin-bottom: 0 !important;
    visibility: visible !important;

    li[data-mobile-only] {
      display: none;
    }
  }
`;
const SocialLinks = styled.div `
  grid-column: 1 / span 3;
  margin-bottom: 40px;

  @media ${from(Device.TabletLarge)} {
    grid-row: 6;
    grid-column: span 1;
    margin-bottom: 0;
  }

  ${SocialLinksStyles.Container} {
    --fgColour: currentColor;

    @media ${until(Device.TabletLarge)} {
      justify-content: center;
    }
  }
`;
const GroupTitle = styled.h2 `
  ${paragraphRegular};

  font-weight: 700;
  margin-bottom: ${fluid(16, 18)};

  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;
const Enquiry = styled.div `
  display: none;

  @media ${from(Device.TabletLarge)} {
    display: block;
    grid-row: 6;
    grid-column: span 1;

    ${LinkList} {
      gap: 8px;
    }
  }
`;
const Copyright = styled.div `
  ${fonts.lato.regular};

  grid-column: 1 / span 3;
  display: flex;
  flex-direction: column;
  font-size: ${fluid(12, 14)};
  line-height: ${fluid(16, 24)};
  gap: 24px;

  p {
    text-align: center;

    span {
      display: inline;

      &:not(:first-child)::before {
        content: '\\2022';
        margin: 0 2ch;
      }
    }

    @media ${until(Device.TabletLarge)} {
      max-width: 48ch;
      margin: 0 auto;
    }

    @media ${from(Device.TabletLarge)} {
      text-align: left;
    }
  }

  ul {
    display: flex;
    justify-content: space-between;
    gap: 16px;

    a {
      ${footerLinkStyles};
    }

    @media ${from(Device.MobileLarge)} {
      justify-content: center;
    }

    @media ${from(Device.TabletLarge)} {
      margin-left: auto;
    }
  }

  @media ${from(Device.TabletLarge)} {
    grid-row: 8;
    grid-column: 1 / span 5;
    margin-top: ${fluid(32, 40)};
    flex-direction: row;
  }
`;
const Divider = styled.div `
  grid-column: 1 / span 5;
  height: 1px;
  background-color: ${brand.primary.purple.dark};

  &[data-first] {
    grid-row: 3;
  }

  &[data-second] {
    grid-row: 5;
    margin-bottom: ${fluid(14, 18)};
  }

  &[data-third] {
    grid-row: 7;
    margin-top: ${fluid(20, 24)};
  }

  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;
const FooterStyles = {
    Container,
    Inner,
    Logo,
    Strapline,
    StraplineLarge,
    ButtonWrapper,
    LinkListContainer,
    LinkListHeader,
    LinkList,
    Divider,
    SocialLinks,
    GroupTitle,
    Enquiry,
    Copyright,
    LinkListTitle,
};
export default FooterStyles;
